/**
 * Home 画面: 部屋を作る・友達と遊ぶ・CPU・キー・公開ロビーの順に 2 列で並べる。
 *
 * - 友達と遊ぶはロビー非表示、部屋を作るは公開で直接作成する。
 * - 参加中の lobby/playing があれば先頭の一式を「元の部屋に戻る」に置き換える (/api/me/rooms/active)。
 * - 「過去のゲーム」リンクは /archive へ遷移、終局済みマッチ一覧を表示する。
 *
 * 未ログイン時:
 *   - 「友達と遊ぶ」「部屋を作る」を押すと /login へ。ログイン後に Home で再度押す。
 *     (auto-create はしない — 「片方がログイン済みならその人が始める」 想定)
 *   - 「キーで入る」はゲストとしてそのまま入室可。
 *   - 「進行中の部屋に戻る」 / 「過去のゲーム」 / UserMenu は出さない (ログイン必須情報のため)。
 */

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { HomeMenuIcon } from '../components/HomeMenuIcon';
import { NewStudyButton } from '../components/NewStudyButton';
import { HomeLobby } from '../components/HomeLobby';
import type { PublicRoom } from '../lib/public-rooms';
import { CODE_LEN, navigateToRoom, normalizeCodeChar } from '../lib/code';
import { GAME_ORDER, gameIconSlug, gameLabel } from '../lib/game-meta';
import { singleGameCpuConfig, startCpuMatch } from '../lib/local-match';
import { navigate, onLinkClick } from '../lib/navigate';
import { executeTurnstile, fetchWithTurnstileRetry } from '../lib/turnstile';
import { useSession } from '../state/session';

interface ActiveRoom {
  id: string;
  code: string;
  state: 'lobby' | 'playing';
}

// ロゴ直下に並ぶゲーム。 並び・表示名はゲーム名の正本 (lib/game-meta) の GAME_ORDER / label。
// game は即開始する対局、id はアイコンの slug。
const HERO_GAMES = GAME_ORDER.map((game) => ({
  game,
  id: gameIconSlug(game),
  label: gameLabel(game),
}));

export function Home({ previewRooms }: { previewRooms?: PublicRoom[] } = {}) {
  const me = useSession((s) => s.me);
  const sessionLoading = useSession((s) => s.loading);
  const [mode, setMode] = useState<'idle' | 'enter' | 'creating'>('idle');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeLookup, setActiveLookup] = useState<{ sub: string; room: ActiveRoom | null } | null>(null);
  const active = me && activeLookup?.sub === me.sub ? activeLookup.room : null;
  const creationReady = previewRooms !== undefined || (!sessionLoading && (!me || activeLookup?.sub === me.sub));

  // NOTE: 以前はここで prefetchTurnstile() を呼んで token を先読みしていたが、
  // managed モードの site key だと execute() が「Verify you are human」 widget を
  // 即表示し、 ページ表示だけで challenge が出っぱなしになる。 token 取得はボタン
  // 押下時の executeTurnstile() に一本化する (challenge はユーザー操作に紐づく)。
  // site key を Invisible モードに切り替えれば prefetch を安全に復活できる。

  useEffect(() => {
    // 未ログインでは /api/me/rooms/active は 401 になるだけなので fetch しない。
    if (!me) {
      setActiveLookup(null);
      return;
    }
    // タイムアウトを設定して、 サーバ無応答でも UI が固まらないようにする。
    let cancelled = false;
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 4_000);
    void fetch('/api/me/rooms/active', { credentials: 'include', signal: ac.signal })
      .then(async (r) => {
        if (r.status === 204) return null;
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as ActiveRoom;
      })
      .then((room) => { if (!cancelled) setActiveLookup({ sub: me.sub, room }); })
      .catch(() => { if (!cancelled) setActiveLookup({ sub: me.sub, room: null }); })
      .finally(() => {
        clearTimeout(timer);
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      ac.abort();
    };
  }, [me]);

  async function createRoom(kind: 'friends' | 'public') {
    // 未ログインなら部屋作成エンドポイントは 401 を返すので、 即 /login に誘導。
    // 元 URL (= /) を redirect として渡し、 ログイン後に Home に戻して再度ボタンを押させる。
    if (!me && previewRooms === undefined) {
      window.location.href = `/login?redirect=${encodeURIComponent('/')}`;
      return;
    }
    if (previewRooms !== undefined) {
      setError('表示サンプルです。実際の部屋は作成しません。');
      return;
    }
    setMode('creating');
    setError(null);
    try {
      const turnstileToken = await executeTurnstile();
      const res = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'cf-turnstile-response': turnstileToken,
        },
        credentials: 'include',
        body: JSON.stringify({ lobby_listed: kind === 'public' }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const { code: newCode } = (await res.json()) as { id: string; code: string };
      navigateToRoom(newCode);
    } catch (e) {
      setError(e instanceof Error ? e.message : '作成に失敗しました');
      setMode('idle');
    }
  }

  function onCodeChange(next: string) {
    const sanitized = next
      .split('')
      .map(normalizeCodeChar)
      .filter((c): c is string => c !== null)
      .slice(0, CODE_LEN)
      .join('');
    setCode(sanitized);
    if (sanitized.length === CODE_LEN) {
      enterRoom(sanitized);
    }
  }

  async function enterRoom(c: string) {
    setError(null);
    try {
      const res = await fetchWithTurnstileRetry(`/api/rooms/${c}`, { credentials: 'include' });
      if (res.status === 404) {
        setError('部屋が見つかりません');
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      navigateToRoom(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'エラー');
    }
  }

  return (
    <main className="app-shell home">
      <header className="home-header">
        <div className="home-mark">
          <h1>
            mixed<span className="home-mark-slash">/</span>games
          </h1>
        </div>
        {me ? (
          <UserMenu name={me.handle ?? me.email} />
        ) : (
          <a
            className="btn-link"
            href={`/login?redirect=${encodeURIComponent('/')}`}
            data-testid="header-login"
          >
            ログイン
          </a>
        )}
      </header>
      <nav className="home-hero-grid" aria-label="ゲーム一覧">
        {HERO_GAMES.map((g) => (
          <button
            key={g.id}
            type="button"
            className="home-hero-game"
            title={`${g.label}でCPUと対局（2時間）`}
            aria-label={`${g.label}でCPUと対局`}
            data-testid={`quick-cpu-${g.id}`}
            onClick={() => {
              try {
                startCpuMatch(singleGameCpuConfig(g.game), 'normal');
              } catch {
                setError('CPU対局を開始できませんでした。保存領域を確認して、もう一度お試しください。');
              }
            }}
          >
            <img src={`${import.meta.env.BASE_URL}icons/games/${g.id}.svg`} width={48} height={48} alt="" />
            <span className="home-hero-game-label">{g.label}</span>
          </button>
        ))}
      </nav>
      <section className="home-actions">
        {active ? (
          <div className="resume-row">
            <button
              className="btn-primary"
              data-testid="resume-active"
              onClick={() => navigateToRoom(active.code)}
            >
              <HomeMenuIcon name="enter-room" />
              <span className="home-row-label">
                元の部屋に戻る
              </span>
              <span className="btn-trailing">{active.code}</span>
            </button>
            <button
              className="btn-link resume-close"
              data-testid="abort-room"
              aria-label="この部屋を閉じる"
              title="この部屋を閉じる (募集を終了)"
              onClick={async () => {
                try {
                  const response = await fetch(`/api/rooms/${active.code}/abort`, {
                    method: 'POST',
                    credentials: 'include',
                  });
                  if (!response.ok) throw new Error(`HTTP ${response.status}`);
                  setActiveLookup(current => current?.room?.code === active.code ? { ...current, room: null } : current);
                } catch {
                  setError('部屋を閉じられませんでした。もう一度お試しください。');
                }
              }}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ) : creationReady ? (
          <>
            <button type="button" className="btn-primary home-row" data-testid="new-room"
              disabled={mode === 'creating'} onClick={() => void createRoom('public')}>
              <HomeMenuIcon name="create-room" />
              <span className="home-row-label">部屋を作る</span>
            </button>
            <button type="button" className="btn-secondary home-row"
              onClick={() => void createRoom('friends')} disabled={mode === 'creating'} data-testid="play-friends">
              <HomeMenuIcon name="new-game" />
              <span className="home-row-label">友達と遊ぶ</span>
            </button>
          </>
        ) : null}
        <button
          className="btn-secondary home-row"
          data-testid="vs-cpu"
          onClick={() => {
            window.history.pushState({}, '', '/local/new');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        >
          <HomeMenuIcon name="cpu" />
          <span className="home-row-label">CPU と対局</span>
        </button>
        {mode !== 'enter' ? (
          <button className="btn-secondary home-row" onClick={() => setMode('enter')}>
            <HomeMenuIcon name="key" />
            <span className="home-row-label">キーで入る</span>
          </button>
        ) : (
          <>
            <div className="code-row">
              <input
                autoFocus
                inputMode="text"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={CODE_LEN}
                value={code}
                onChange={(e) => onCodeChange(e.target.value)}
                placeholder="ABCDEF"
                aria-label="キー"
              />
              <button className="btn-link" onClick={() => setMode('idle')}>
                キャンセル
              </button>
            </div>
            {error && (
              <p className="error code-row-error" data-testid="room-enter-error">
                {error}
              </p>
            )}
          </>
        )}
        {creationReady && !active && <HomeLobby previewRooms={previewRooms} />}
        {me && (
          <a className="btn-link home-row" href="/archive" data-testid="past-games">
            <HomeMenuIcon name="archive" />
            <span className="home-row-label">過去のゲーム</span>
          </a>
        )}
        <a className="btn-link home-row" href="/tutorial" data-testid="tutorial-index">
          <HomeMenuIcon name="learn" />
          <span className="home-row-label">ルールを学ぶ</span>
        </a>
        <NewStudyButton />
        <a className="btn-link home-row" href="/puzzle" data-testid="puzzle-index">
          <HomeMenuIcon name="puzzle" />
          <span className="home-row-label">詰めパズル</span>
        </a>
        <a href="/experiments/mix" data-testid="shared-mix" onClick={onLinkClick('/experiments/mix')} className="btn-link home-row">
          <HomeMenuIcon name="mix" />
          <span className="home-row-label">ごちゃまぜ</span>
        </a>
        {/* enter モードのときはコード入力直下に出すので、 ここは create / 一般エラー専用。 */}
        {error && mode !== 'enter' && <p className="error">{error}</p>}
      </section>
      <footer className="home-footer">
        <p className="home-footer-discord">
          <a
            href="https://discord.gg/TwvrMKhcHv"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="discord-link"
          >
            Join our Discord
            <svg
              className="discord-mark"
              viewBox="0 0 127.14 96.36"
              width={16}
              height={12}
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
            </svg>
          </a>
        </p>
        <small>
          Chess king SVG © Colin M.L. Burnett (
          <a href="https://en.wikipedia.org/wiki/User:Cburnett/GFDL_images/Chess" target="_blank" rel="noopener noreferrer">
            Cburnett
          </a>
          ), used under the BSD license. Provided “as is”, without warranty of any kind, express or implied.
        </small>
      </footer>
    </main>
  );
}

/** Home の右上に出すユーザー名 + メニュー (設定 / ログアウト)。
 *  クリックで dropdown、 outside-click か Escape で閉じる。 NavMenu と同じ流儀。 */
function UserMenu({ name }: { name: string }) {
  const logout = useSession((s) => s.logout);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={ref}>
      <button
        className="user-menu-trigger"
        aria-label="ユーザーメニュー"
        aria-expanded={open}
        data-testid="user-menu"
        onClick={() => setOpen((v) => !v)}
      >
        <small>{name}</small>
      </button>
      {open && (
        <div className="user-menu-panel" role="menu">
          <button
            className="user-menu-item"
            role="menuitem"
            data-testid="user-menu-settings"
            onClick={() => {
              setOpen(false);
              navigate('/settings');
            }}
          >
            設定
          </button>
          <button
            className="user-menu-item danger"
            role="menuitem"
            data-testid="user-menu-logout"
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate('/');
            }}
          >
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
}
